import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Save, FileDown, History as HistoryIcon, Share2, RotateCcw, ImagePlus, X, RefreshCw } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import iiApi from "@/lib/incomingInspectionApi";
import { formatApiError } from "@/lib/api";
import { computeSPC } from "@/lib/incomingInspectionSpc";
import { rowStatus, computeRowLimits, fmtLimit, splitDimensionCell, splitTrailingTolerance } from "@/lib/incomingInspectionTolerance";
import { exportReportToPDF } from "@/lib/incomingInspectionPdf";
import ControlChart from "@/components/incoming-inspection/ControlChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SAMPLE_COUNT = 5;
const MAX_DIMENSIONS = 30;
const MAX_IMAGES = 8;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

const QC_KEYS = ["visual", "fitment", "surface_finish", "thread1", "thread2"];
const QC_LABELS = { visual: "Visual", fitment: "Fitment", surface_finish: "Surface Finish", thread1: "Thread 1", thread2: "Thread 2" };

function blankMeta() {
  return {
    title: "", process_name: "", part_number: "", part_description: "",
    shift_code: "", employee_code: "", drawing_revision: "", operator_name: "",
    unit_of_measure: "", tolerance_note: "", lot_qty: "", supplier_name: "",
    lsl: "", target_value: "", notes: "",
  };
}
function blankQc() {
  return Object.fromEntries(QC_KEYS.map((k) => [k, { remarks: "", result: "ACCEPTED" }]));
}
function blankRow() {
  return { dimension: "", tolerance: "", samples: Array(SAMPLE_COUNT).fill("") };
}
function ncrKey(source, dimIndex) {
  return `${source}:${dimIndex || ""}`;
}

function StatusBadge({ status }) {
  if (status === "ACCEPTED") return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Accepted</span>;
  if (status === "NC") return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">NC</span>;
  return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">—</span>;
}

function NcrCell({ savedReportId, ncrMap, source, dimIndex, backPath }) {
  if (!savedReportId) return <span className="text-[11px] text-slate-400 italic">Save report to raise NCR</span>;
  const entry = ncrMap[ncrKey(source, dimIndex)];
  const qs = new URLSearchParams({ report_id: savedReportId, source, back: backPath });
  if (dimIndex) qs.set("dimension_index", String(dimIndex));
  const href = `/quality/incoming-inspection/ncr?${qs.toString()}`;
  if (!entry) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
        Raise NCR
      </a>
    );
  }
  const isClosed = entry.status === "CLOSED";
  const cls = !entry.is_complete ? "bg-slate-100 text-slate-500" : isClosed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
  const label = !entry.is_complete ? `${entry.ncr_no} · Draft` : `${entry.ncr_no} · ${isClosed ? "Closed" : "Open"}`;
  return (
    <a href={href} target="_blank" rel="noreferrer">
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
    </a>
  );
}

export default function IncomingInspectionNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  // Phase 43 — Final Inspection launches this same detailed entry screen
  // against a specific production job instead of a generic incoming lot.
  const stageParam = searchParams.get("stage") || "incoming";
  const isFinalInspection = stageParam === "final_inspection";
  const jobId = searchParams.get("job_id") || null;
  const jobCode = searchParams.get("job_code") || "";

  const [meta, setMeta] = useState(blankMeta());
  const [qc, setQc] = useState(blankQc());
  const [rows, setRows] = useState([]);
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [savedReportId, setSavedReportId] = useState(null);
  const [editingReportId, setEditingReportId] = useState(null);
  const [ncrMap, setNcrMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [importError, setImportError] = useState("");

  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  const partLookupTimer = useRef(null);
  const lastPartLookup = useRef("");

  const backPath = "/quality/incoming-inspection";

  // ---------------- Initial load ----------------
  useEffect(() => {
    if (editId) {
      (async () => {
        try {
          const { report: r, ncrs } = await iiApi.getReport(editId);
          setMeta({
            title: r.title || "", process_name: r.process_name || "", part_number: r.part_number || "",
            part_description: r.part_description || "", shift_code: r.shift_code || "", employee_code: r.employee_code || "",
            drawing_revision: r.drawing_revision || "", operator_name: r.operator_name || "", unit_of_measure: r.unit_of_measure || "",
            tolerance_note: r.tolerance_note || "", lot_qty: r.lot_qty ?? "", supplier_name: r.supplier_name || "",
            lsl: r.lsl ?? "", target_value: r.target_value ?? "", notes: r.notes || "",
          });
          setQc({
            visual: { remarks: r.visual_remarks || "", result: r.visual_result || "ACCEPTED" },
            fitment: { remarks: r.fitment_remarks || "", result: r.fitment_result || "ACCEPTED" },
            surface_finish: { remarks: r.surface_finish_remarks || "", result: r.surface_finish_result || "ACCEPTED" },
            thread1: { remarks: r.thread1_remarks || "", result: r.thread1_result || "ACCEPTED" },
            thread2: { remarks: r.thread2_remarks || "", result: r.thread2_result || "ACCEPTED" },
          });
          setImages(Array.isArray(r.images) ? r.images : []);
          setRows((r.rows || []).map((row) => ({
            dimension: row.dimension || "", tolerance: row.tolerance || "",
            samples: Array.from({ length: SAMPLE_COUNT }, (_, i) => (row.samples && row.samples[i] !== undefined ? row.samples[i] : "")),
          })));
          const map = {};
          (ncrs || []).forEach((n) => { map[ncrKey(n.source, n.dimension_index)] = n; });
          setNcrMap(map);
          setSavedReportId(editId);
          setEditingReportId(editId);
          toast.success(`Editing "${r.title}" — make your changes and click Update Report.`);
        } catch (e) {
          toast.error("Could not load that report for editing: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
          setLoadingEdit(false);
        }
      })();
    } else {
      setRows(Array.from({ length: 5 }, () => blankRow()));
      const prefillPart = searchParams.get("part_number") || "";
      const prefillName = searchParams.get("part_name") || "";
      setMeta((m) => ({
        ...m,
        operator_name: user?.name || "",
        process_name: isFinalInspection ? "Final Inspection" : m.process_name,
        title: isFinalInspection && jobCode ? `Final Inspection — ${jobCode}` : m.title,
        part_number: prefillPart || m.part_number,
        part_description: prefillName || m.part_description,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ---------------- Derived SPC stats ----------------
  const validSubgroups = useMemo(
    () => rows.map((r) => r.samples.filter((v) => v !== "" && v !== null && v !== undefined && !isNaN(v)).map(Number)).filter((sg) => sg.length >= 1),
    [rows]
  );
  const lastStats = useMemo(() => {
    if (validSubgroups.length < 2) return null;
    const lsl = meta.lsl === "" || meta.lsl === null ? null : parseFloat(meta.lsl);
    return computeSPC(validSubgroups, null, isNaN(lsl) ? null : lsl);
  }, [validSubgroups, meta.lsl]);

  // ---------------- Row / dimension handlers ----------------
  const addDimension = () => {
    if (rows.length >= MAX_DIMENSIONS) { toast.error(`Maximum of ${MAX_DIMENSIONS} dimensions per report reached.`); return; }
    setRows((rs) => [...rs, blankRow()]);
  };
  const removeDimension = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const updateSample = (idx, sIdx, value) => setRows((rs) => rs.map((r, i) => {
    if (i !== idx) return r;
    const samples = r.samples.slice();
    samples[sIdx] = value === "" ? "" : parseFloat(value);
    return { ...r, samples };
  }));

  // ---------------- Images ----------------
  const readImageAsDataUri = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that image file."));
    reader.readAsDataURL(file);
  });

  const handleImageFiles = async (fileList) => {
    setImageError("");
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (images.length + files.length > MAX_IMAGES) { setImageError(`You can attach up to ${MAX_IMAGES} images per report.`); return; }
    const next = [];
    for (const file of files) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) { setImageError("Please choose PNG, JPEG, WEBP, or GIF images only."); continue; }
      if (file.size > MAX_IMAGE_BYTES) { setImageError("One or more images were too large — please choose images under 5MB each."); continue; }
      try { next.push(await readImageAsDataUri(file)); }
      catch (err) { setImageError(err.message || "Could not read one of the image files."); }
    }
    if (next.length) setImages((imgs) => [...imgs, ...next]);
  };
  const removeImage = (idx) => { setImages((imgs) => imgs.filter((_, i) => i !== idx)); setImageError(""); };

  // ---------------- Part Number auto-fill ----------------
  const applyPartSpec = (spec) => {
    if (spec.part_name) setMeta((m) => ({ ...m, part_description: spec.part_name }));
    if (!spec || !Array.isArray(spec.dimensions) || spec.dimensions.length === 0) return;
    const n = Math.min(MAX_DIMENSIONS, spec.dimensions.length);
    const newRows = [];
    for (let i = 0; i < n; i++) {
      const dim = spec.dimensions[i] || {};
      const lastSamples = (spec.last_samples && spec.last_samples[String(i + 1)]) || [];
      newRows.push({
        dimension: dim.dimension_text || "", tolerance: dim.tolerance_text || "",
        samples: Array.from({ length: SAMPLE_COUNT }, (_, s) => (lastSamples.length ? (lastSamples[s] ?? "") : "")),
      });
    }
    setRows(newRows);
    if (spec.dimensions.length > MAX_DIMENSIONS) {
      toast.error(`Only the first ${MAX_DIMENSIONS} dimensions were loaded — the report limit is ${MAX_DIMENSIONS}.`);
    }
    setQc((q) => ({
      ...q,
      visual: spec.visual ? { ...q.visual, remarks: spec.visual } : q.visual,
      fitment: spec.fitment ? { ...q.fitment, remarks: spec.fitment } : q.fitment,
      surface_finish: spec.surface_finish ? { ...q.surface_finish, remarks: spec.surface_finish } : q.surface_finish,
    }));
  };

  const triggerPartLookup = (partNumber) => {
    if (!partNumber || partNumber === lastPartLookup.current) return;
    clearTimeout(partLookupTimer.current);
    partLookupTimer.current = setTimeout(async () => {
      lastPartLookup.current = partNumber;
      try {
        const res = await iiApi.getPartSpec(partNumber);
        if (res && res.found) {
          applyPartSpec(res);
          toast.success(`Loaded saved dimensions & tolerances for ${partNumber}.`);
        } else {
          toast.error(`No saved dimensions found for "${partNumber}" yet. Import this part or add dimensions manually.`);
        }
      } catch (err) {
        toast.error(`Could not load part data: ${formatApiError(err?.response?.data?.detail) || err.message}`);
      }
    }, 300);
  };

  // ---------------- Import Excel/CSV ----------------
  function parseInspectionWorkbook(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Could not read that file."));
      reader.onload = () => {
        try {
          const data = new Uint8Array(reader.result);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, raw: true });
          if (sheetRows.length < 3) return resolve([]);

          const headerRow = sheetRows[1];
          const dimCols = [];
          let visualCol = -1, fitmentCol = -1, finishCol = -1;
          headerRow.forEach((val, colIdx) => {
            if (val === null || val === "") return;
            if (typeof val === "number") dimCols.push({ col: colIdx, index: val });
            else if (/visual/i.test(String(val))) visualCol = colIdx;
            else if (/fitment/i.test(String(val))) fitmentCol = colIdx;
            else if (/surface/i.test(String(val))) finishCol = colIdx;
          });

          const parts = [];
          for (let r = 2; r < sheetRows.length; r++) {
            const row = sheetRows[r];
            if (!row) continue;
            const code = row[0] ? String(row[0]).trim() : "";
            const name = row[1] ? String(row[1]).trim() : "";
            if (!code) continue;
            const dimensions = [];
            dimCols.forEach(({ col, index }) => {
              const cell = row[col];
              if (cell !== null && cell !== undefined && String(cell).trim() !== "") {
                const split = splitDimensionCell(cell);
                dimensions.push({ index, dimension: split.dimension, tolerance: split.tolerance });
              }
            });
            if (dimensions.length === 0) continue;
            parts.push({
              code, name,
              visual: visualCol >= 0 ? row[visualCol] || "" : "",
              fitment: fitmentCol >= 0 ? row[fitmentCol] || "" : "",
              surface_finish: finishCol >= 0 ? row[finishCol] || "" : "",
              dimensions,
            });
          }
          resolve(parts);
        } catch (err) {
          reject(new Error("Could not parse that file — " + (err?.message || "unknown error") + ". Please check it matches the Inspection Criteria Report format."));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportError("");
    try {
      const parts = await parseInspectionWorkbook(file);
      if (parts.length === 0) { setImportError("No part rows were found in that file. Check the format and try again."); return; }
      await iiApi.importPartSpecs(parts);
      toast.success(`Imported ${parts.length} part number(s) into the tolerance library.`);
      const currentPart = (meta.part_number || "").trim().toUpperCase();
      const match = parts.find((p) => p.code.toUpperCase() === currentPart);
      if (match) {
        applyPartSpec({
          part_name: match.name,
          dimensions: match.dimensions.map((d) => ({ dimension_text: d.dimension, tolerance_text: d.tolerance })),
          last_samples: {},
        });
      }
    } catch (err) {
      setImportError(err.message || formatApiError(err?.response?.data?.detail) || "Could not import that file.");
    } finally {
      e.target.value = "";
    }
  };

  // ---------------- NCR refresh ----------------
  const refreshNcrs = async () => {
    if (!savedReportId) return;
    try {
      const res = await iiApi.listNcrs({ report_id: savedReportId });
      const map = {};
      (res.ncrs || []).forEach((n) => { map[ncrKey(n.source, n.dimension_index)] = n; });
      setNcrMap(map);
      toast.success("NCR statuses refreshed.");
    } catch (err) {
      toast.error("Could not refresh NCR statuses: " + (formatApiError(err?.response?.data?.detail) || err.message));
    }
  };

  // ---------------- Save / Update ----------------
  const buildPayload = () => ({
    title: meta.title.trim(),
    stage: stageParam,
    job_id: jobId,
    job_code: jobCode,
    process_name: meta.process_name.trim(),
    part_number: meta.part_number.trim(),
    part_description: meta.part_description.trim(),
    shift_code: meta.shift_code.trim(),
    employee_code: meta.employee_code.trim(),
    drawing_revision: meta.drawing_revision.trim(),
    operator_name: meta.operator_name.trim(),
    unit_of_measure: meta.unit_of_measure.trim(),
    subgroup_size: SAMPLE_COUNT,
    tolerance_note: meta.tolerance_note.trim(),
    lot_qty: meta.lot_qty === "" ? null : Number(meta.lot_qty),
    supplier_name: meta.supplier_name.trim(),
    lsl: meta.lsl === "" ? null : Number(meta.lsl),
    target_value: meta.target_value === "" ? null : Number(meta.target_value),
    notes: meta.notes.trim(),
    images_data: images,
    visual_remarks: qc.visual.remarks.trim(), visual_result: qc.visual.result,
    fitment_remarks: qc.fitment.remarks.trim(), fitment_result: qc.fitment.result,
    surface_finish_remarks: qc.surface_finish.remarks.trim(), surface_finish_result: qc.surface_finish.result,
    thread1_remarks: qc.thread1.remarks.trim(), thread1_result: qc.thread1.result,
    thread2_remarks: qc.thread2.remarks.trim(), thread2_result: qc.thread2.result,
    subgroups: rows.map((r) => r.samples.filter((v) => v !== "" && v !== null && !isNaN(v)).map(Number)),
    rows: rows.map((r) => ({ dimension: r.dimension || "", tolerance: r.tolerance || "" })),
  });

  const handleSave = async () => {
    if (!meta.title.trim()) return toast.error("Please enter a report title.");
    if (rows.length === 0) return toast.error("Add at least one dimension before saving.");
    if (rows.length > MAX_DIMENSIONS) return toast.error(`No more than ${MAX_DIMENSIONS} dimensions are allowed per report.`);
    const blankDimIdx = rows.findIndex((r) => !String(r.dimension || "").trim());
    if (blankDimIdx !== -1) return toast.error(`Dimension #${blankDimIdx + 1} is missing a value. Fill in every dimension before saving.`);
    const incompleteIdx = rows.findIndex((r) => r.samples.some((v) => v === "" || v === null || isNaN(v)));
    if (incompleteIdx !== -1) return toast.error(`Dimension #${incompleteIdx + 1} is missing sample readings. All 5 samples are required for every dimension.`);
    if (validSubgroups.length < 2) return toast.error("At least 2 dimensions are required to compute control limits.");

    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingReportId) {
        const res = await iiApi.updateReport(editingReportId, payload);
        setSavedReportId(editingReportId);
        await refreshNcrs();
        toast[res.has_nc ? "error" : "success"](
          res.has_nc ? "Report updated — NC still present. Use the NCR Report column to raise/update NCRs." : "Report updated successfully."
        );
      } else {
        const res = await iiApi.saveReport(payload);
        setSavedReportId(res.report_id);
        setNcrMap({});
        toast[res.has_nc ? "error" : "success"](
          res.has_nc ? "Report saved — NC detected. Use the NCR Report column to raise NCRs." : "Report saved successfully."
        );
        const email = res.email || {};
        const to = (email.to || []).join(", ");
        if (email.status === "sent") toast.success(`Email sent to ${to}.`);
        else if (email.status === "queued") toast.warning(`Email NOT sent — no mail account is configured on the server (SMTP_HOST / RESEND_API_KEY). It was queued for ${to}.`, { duration: 10000 });
        else if (email.status === "failed") toast.error(`Email to ${to} failed: ${email.error || "unknown error"}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Download PDF ----------------
  const rowsForPdf = () => rows
    .filter((r) => r.samples.some((v) => v !== "" && v !== null && !isNaN(v)))
    .map((r, i) => {
      const base = {
        dimension: r.dimension || "", tolerance: r.tolerance || "",
        samples: r.samples.filter((v) => v !== "" && v !== null && !isNaN(v)).map(Number),
        status: rowStatus(r),
      };
      const entry = ncrMap[ncrKey("dimension", i + 1)];
      return entry ? { ...base, ncr: { ncr_no: entry.ncr_no, status: entry.status, is_complete: entry.is_complete, detail: entry } } : base;
    });

  const qcForExport = () => {
    const out = {};
    QC_KEYS.forEach((key) => {
      const base = { result: qc[key].result, remarks: qc[key].remarks };
      if (qc[key].result === "NC") {
        const entry = ncrMap[ncrKey(key, null)];
        if (entry) base.ncr = { ncr_no: entry.ncr_no, status: entry.status, is_complete: entry.is_complete, detail: entry };
      }
      out[key] = base;
    });
    return out;
  };

  const handleDownloadPdf = async () => {
    if (!lastStats) return toast.error("Add at least 2 dimensions to generate a report.");
    setDownloading(true);
    try {
      await exportReportToPDF({
        meta: { ...meta, images_data: images, created_at: new Date().toLocaleString() },
        stats: lastStats,
        rows: rowsForPdf(),
        images,
        qc: qcForExport(),
      });
      toast.success("PDF downloaded.");
    } catch (err) {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // ---------------- Share to WhatsApp ----------------
  const handleShare = () => {
    if (!lastStats) return toast.error("Add at least 2 dimensions before sharing.");
    const s = lastStats;
    const fmt = (v, d = 3) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));
    const text = `*SPC Report: ${meta.title || "Untitled"}*
Process: ${meta.process_name || "—"}
Part No: ${meta.part_number || "—"}

Grand Avg (X̿): ${fmt(s.xDoubleBar)}
Avg Range (R̄): ${fmt(s.rBar)}
UCL/LCL (X̄): ${fmt(s.uclX)} / ${fmt(s.lclX)}
Cp: ${fmt(s.cp)}  Cpk: ${fmt(s.cpk)}
Status: ${s.outOfControlCount > 0 ? "Unstable (" + s.outOfControlCount + " out-of-control pts)" : "In Control"}

Generated with Incoming/In-Process Inspection — Canares Automation ERP`;
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  };

  // ---------------- Reset ----------------
  const handleReset = () => {
    if (!window.confirm("Clear all report details and data? This cannot be undone.")) return;
    setMeta({ ...blankMeta(), operator_name: user?.name || "" });
    setImages([]);
    setImageError("");
    setQc(blankQc());
    setRows([]);
    setEditingReportId(null);
    nav("/quality/incoming-inspection", { replace: true });
    toast.success("Form cleared.");
  };

  if (loadingEdit) {
    return <div className="text-sm text-slate-500 py-10 text-center">Loading report…</div>;
  }

  return (
    <div className="space-y-6" data-testid="incoming-inspection-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            {isFinalInspection ? "Final Inspection — Detailed Report" : "Incoming/In-Process Inspection"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isFinalInspection
              ? <>Statistical Process Control &amp; X̄–R Analysis for {jobCode ? <>job <span className="mono font-semibold">{jobCode}</span></> : "this Final Inspection batch"}</>
              : "Statistical Process Control & X̄–R Analysis"}
          </p>
        </div>
        <div className="flex gap-2">
          {jobId && (
            <Link to="/production-flow/final_inspection">
              <Button variant="outline">Back to Final Inspection</Button>
            </Link>
          )}
          <Link to="/quality/incoming-inspection/history">
            <Button variant="outline"><HistoryIcon className="w-4 h-4 mr-1.5" />Report History</Button>
          </Link>
          <Link to="/quality/incoming-inspection/ncr-register">
            <Button variant="outline">NCR Register</Button>
          </Link>
        </div>
      </div>

      {/* Report Details */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-base font-bold text-slate-800">Report Details</h2>
          <p className="text-xs text-slate-500 mt-0.5">Define the process being studied and the specification limits.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><Label>Report Title *</Label><Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} placeholder="e.g. Shaft Diameter — Line 3" /></div>
          <div><Label>Process Name</Label><Input value={meta.process_name} onChange={(e) => setMeta({ ...meta, process_name: e.target.value })} placeholder="e.g. CNC Turning" /></div>
          <div>
            <Label>Part Number</Label>
            <Input
              value={meta.part_number}
              onChange={(e) => setMeta({ ...meta, part_number: e.target.value })}
              onBlur={(e) => triggerPartLookup(e.target.value.trim())}
              placeholder="e.g. PN-4471-A"
            />
          </div>
          <div><Label>Part Description</Label><Input value={meta.part_description} readOnly placeholder="auto-fills from Part Number" className="bg-slate-50" /></div>
          <div><Label>Shift Code</Label><Input value={meta.shift_code} onChange={(e) => setMeta({ ...meta, shift_code: e.target.value })} placeholder="e.g. A / B / C" /></div>
          <div><Label>Employee Code</Label><Input value={meta.employee_code} onChange={(e) => setMeta({ ...meta, employee_code: e.target.value })} placeholder="e.g. EMP-1042" /></div>
          <div><Label>Revision of Drawing</Label><Input value={meta.drawing_revision} onChange={(e) => setMeta({ ...meta, drawing_revision: e.target.value })} placeholder="e.g. Rev. C" /></div>
          <div><Label>Inspector</Label><Input value={meta.operator_name} onChange={(e) => setMeta({ ...meta, operator_name: e.target.value })} placeholder="e.g. J. Reyes" /></div>
          <div><Label>Unit of Measure</Label><Input value={meta.unit_of_measure} onChange={(e) => setMeta({ ...meta, unit_of_measure: e.target.value })} placeholder="e.g. mm" /></div>
          <div><Label>Tolerance</Label><Input value={meta.tolerance_note} onChange={(e) => setMeta({ ...meta, tolerance_note: e.target.value })} placeholder="e.g. +/-0.05 or +0.1/-0.00" /></div>
          <div><Label>LOT Qty</Label><Input type="number" step="any" value={meta.lot_qty} onChange={(e) => setMeta({ ...meta, lot_qty: e.target.value })} placeholder="e.g. 500" /></div>
          <div><Label>Supplier Name</Label><Input value={meta.supplier_name} onChange={(e) => setMeta({ ...meta, supplier_name: e.target.value })} placeholder="e.g. ABC Components Pvt. Ltd." /></div>
          <div><Label>Lower Spec Limit (LSL)</Label><Input type="number" step="any" value={meta.lsl} onChange={(e) => setMeta({ ...meta, lsl: e.target.value })} placeholder="optional" /></div>
          <div><Label>Target Value</Label><Input type="number" step="any" value={meta.target_value} onChange={(e) => setMeta({ ...meta, target_value: e.target.value })} placeholder="optional" /></div>
        </div>
        <div className="mt-4">
          <Label>Notes</Label>
          <Textarea rows={2} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} placeholder="Any observations, process changes, or context for this study..." />
        </div>
      </section>

      {/* Images */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-base font-bold text-slate-800">Process / Part Images</h2>
          <p className="text-xs text-slate-500 mt-0.5">Optionally attach photos of the part or process being studied. Included in the saved report and at the end of the PDF.</p>
        </div>
        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple hidden
          onChange={(e) => { handleImageFiles(e.target.files); e.target.value = ""; }} />
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${dragOver ? "border-blue-400 bg-blue-50/40" : "border-slate-200"}`}
          onClick={() => images.length === 0 && fileInputRef.current?.click()}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) handleImageFiles(e.dataTransfer.files); }}
        >
          {images.length === 0 ? (
            <div className="text-slate-500">
              <ImagePlus className="w-7 h-7 mx-auto mb-2 text-slate-400" />
              <p className="text-sm"><strong>Click to upload</strong> or drag images here</p>
              <span className="text-xs">PNG, JPEG, WEBP, or GIF — up to 5MB each, up to {MAX_IMAGES} images</span>
            </div>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-wrap gap-3 justify-center">
                {images.map((uri, idx) => (
                  <div key={idx} className="relative">
                    <img src={uri} alt={`Process ${idx + 1}`} className="w-24 h-24 object-cover rounded-md border border-slate-200" />
                    <button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 shadow"><X className="w-3 h-3 text-slate-600" /></button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => fileInputRef.current?.click()}>
                <Plus className="w-3.5 h-3.5 mr-1" />Add More Images
              </Button>
            </div>
          )}
        </div>
        {imageError && <p className="text-xs text-red-600 mt-2">{imageError}</p>}
      </section>

      {/* Dimension Data Entry */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-slate-800">Dimension Data Entry</h2>
            <p className="text-xs text-slate-500 mt-0.5">Enter 5 sample readings for every dimension — every dimension you add must be filled in. Up to {MAX_DIMENSIONS} dimensions per report.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleImportFile} />
            <Button type="button" variant="outline" size="sm" onClick={() => importInputRef.current?.click()} title="Import dimensions, tolerances & samples from Excel/CSV">
              <Upload className="w-3.5 h-3.5 mr-1" />Import
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={refreshNcrs} title="Refresh NCR Report statuses for this inspection">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh NCR
            </Button>
            <Button type="button" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={addDimension}>
              <Plus className="w-3.5 h-3.5 mr-1" />Add Dimension
            </Button>
          </div>
        </div>
        {importError && <p className="text-xs text-red-600 mb-2">{importError}</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2 w-8">#</th>
                <th className="py-2 pr-2 w-28">Dimension</th>
                <th className="py-2 pr-2 w-32">Tolerance</th>
                <th className="py-2 pr-2 w-16">Min</th>
                <th className="py-2 pr-2 w-16">Max</th>
                {Array.from({ length: SAMPLE_COUNT }, (_, i) => <th key={i} className="py-2 pr-2 w-20">Sample {i + 1}</th>)}
                <th className="py-2 pr-2 w-20">Status</th>
                <th className="py-2 pr-2 w-32">NCR Report</th>
                <th className="py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={SAMPLE_COUNT + 8} className="py-8 text-center text-slate-400">No dimensions yet — click "Add Dimension" to begin entering data.</td></tr>
              )}
              {rows.map((row, idx) => {
                const status = rowStatus(row);
                const limits = computeRowLimits(row);
                return (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-1.5 pr-2 text-slate-500 font-semibold">#{idx + 1}</td>
                    <td className="py-1.5 pr-2">
                      <Input
                        className="h-8 text-xs"
                        value={row.dimension}
                        onChange={(e) => updateRow(idx, { dimension: e.target.value })}
                        onBlur={(e) => {
                          // If the whole spec (e.g. "A/F44+/-0.10") got typed
                          // straight into this box and Tolerance is still
                          // empty, split the tolerance off automatically
                          // instead of leaving it stuck un-evaluable here.
                          if (row.tolerance) return;
                          const split = splitTrailingTolerance(e.target.value);
                          if (split) updateRow(idx, { dimension: split.dimension, tolerance: split.tolerance });
                        }}
                        placeholder="e.g. 36"
                      />
                    </td>
                    <td className="py-1.5 pr-2"><Input className="h-8 text-xs" value={row.tolerance} onChange={(e) => updateRow(idx, { tolerance: e.target.value })} placeholder="e.g. +/-0.05" /></td>
                    <td className="py-1.5 pr-2 text-slate-600">{limits ? fmtLimit(limits.min) : "—"}</td>
                    <td className="py-1.5 pr-2 text-slate-600">{limits ? fmtLimit(limits.max) : "—"}</td>
                    {row.samples.map((val, sIdx) => (
                      <td key={sIdx} className="py-1.5 pr-2">
                        <Input type="number" step="any" className="h-8 text-xs" value={val} onChange={(e) => updateSample(idx, sIdx, e.target.value)} placeholder="0.00" />
                      </td>
                    ))}
                    <td className="py-1.5 pr-2"><StatusBadge status={status} /></td>
                    <td className="py-1.5 pr-2">
                      {status === "NC" ? <NcrCell savedReportId={savedReportId} ncrMap={ncrMap} source="dimension" dimIndex={idx + 1} backPath={backPath} /> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-1.5">
                      <button type="button" onClick={() => removeDimension(idx)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Visual / Fitment / Surface Finish */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-base font-bold text-slate-800">Visual, Fitment &amp; Surface Finish</h2>
          <p className="text-xs text-slate-500 mt-0.5">Record the qualitative inspection result for each check. Marking any check "NC" flags the whole report as non-conforming.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2 w-32">Check</th><th className="py-2 pr-2">Remarks</th><th className="py-2 pr-2 w-32">Result</th><th className="py-2 pr-2 w-36">NCR Report</th>
              </tr>
            </thead>
            <tbody>
              {QC_KEYS.map((key) => (
                <tr key={key} className="border-b border-slate-100">
                  <td className="py-2 pr-2 font-semibold text-slate-700">{QC_LABELS[key]}</td>
                  <td className="py-2 pr-2"><Input className="h-8 text-xs" value={qc[key].remarks} onChange={(e) => setQc({ ...qc, [key]: { ...qc[key], remarks: e.target.value } })} placeholder="e.g. No dents, scratches or burrs" /></td>
                  <td className="py-2 pr-2">
                    <Select value={qc[key].result} onValueChange={(v) => setQc({ ...qc, [key]: { ...qc[key], result: v } })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="ACCEPTED">Accepted</SelectItem><SelectItem value="NC">NC</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 pr-2">
                    {qc[key].result === "NC" ? <NcrCell savedReportId={savedReportId} ncrMap={ncrMap} source={key} dimIndex={null} backPath={backPath} /> : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Control Chart Results */}
      {lastStats && (
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="mb-4">
            <h2 className="text-base font-bold text-slate-800">Control Chart Results</h2>
            <p className="text-xs text-slate-500 mt-0.5">Live statistics computed from your dimension data.</p>
          </div>
          <StatGrid stats={lastStats} />
          <ControlChart title="X̄ Chart (Dimension Averages)" points={lastStats.means} ucl={lastStats.uclX} lcl={lastStats.lclX} cl={lastStats.xDoubleBar} flags={lastStats.flags} />
          <ControlChart title="R Chart (Dimension Ranges)" points={lastStats.ranges} ucl={lastStats.uclR} lcl={lastStats.lclR} cl={lastStats.rBar} flags={lastStats.flags} />
        </section>
      )}

      {/* Action bar */}
      <section className="flex flex-wrap gap-2">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving} data-testid="ii-save-btn">
          <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : editingReportId ? "Update Report" : "Save Report"}
        </Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDownloadPdf} disabled={downloading}>
          <FileDown className="w-4 h-4 mr-1.5" />Download PDF
        </Button>
        <Button variant="outline" onClick={handleShare}><Share2 className="w-4 h-4 mr-1.5" />Share to WhatsApp</Button>
        <Button variant="outline" onClick={handleReset}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
      </section>
    </div>
  );
}

function StatGrid({ stats: s }) {
  const fmt = (v, d = 3) => (v === null || v === undefined || isNaN(v) ? "—" : Number(v).toFixed(d));
  const tiles = [
    { label: "Grand Avg (X̿)", value: fmt(s.xDoubleBar) },
    { label: "Avg Range (R̄)", value: fmt(s.rBar) },
    { label: "UCL (X̄)", value: fmt(s.uclX) },
    { label: "LCL (X̄)", value: fmt(s.lclX) },
    { label: "Cp", value: fmt(s.cp), cls: s.cp == null ? "" : s.cp >= 1.33 ? "good" : "warn" },
    { label: "Cpk", value: fmt(s.cpk), cls: s.cpk == null ? "" : s.cpk >= 1.33 ? "good" : "warn" },
    { label: "UCL (R)", value: fmt(s.uclR) },
    { label: "LCL (R)", value: fmt(s.lclR) },
    { label: "Dimensions", value: String(s.means.length) },
    { label: "Sample Size (n)", value: String(s.n) },
    { label: "Out-of-Control Pts", value: String(s.outOfControlCount), cls: s.outOfControlCount > 0 ? "warn" : "good" },
    { label: "Process Status", value: s.outOfControlCount > 0 ? "Unstable" : "In Control", cls: s.outOfControlCount > 0 ? "warn" : "good" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className={`rounded-lg border p-3 ${t.cls === "warn" ? "border-red-200 bg-red-50" : t.cls === "good" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</div>
          <div className={`text-lg font-bold mt-0.5 ${t.cls === "warn" ? "text-red-700" : t.cls === "good" ? "text-emerald-700" : "text-slate-800"}`}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}
